import * as React from "react";
import { CssBaseline, Box } from "@mui/material";
import { useParams } from "react-router-dom";

import { getIndividualVenue } from "../helpers/onCallFunctions.ts";
import { ParentVenue, ChildVenue } from "../types/dataTypes.ts";

import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";
import { TitleElement } from "../components/TitleElement/TitleElement.tsx";
import { LandingPageDescriptionBoxes } from "../components/LandingPageDescriptionBoxes/LandingPageDescriptionBoxes.tsx";
import { LinkToCinemaSelectPage } from "../components/TextLinks/LinkToCinemaSelectPage.tsx";
import { LoadingElementInBox } from "../components/Loading/LoadingElement.tsx";
import { NotFoundPage } from "./NotFoundPage.tsx";

import { useFilmListingsController } from "../hooks/useFilmListingsController.ts";
import { FilmListingsSection } from "../components/FilmListingsSection/FilmListingsSection.tsx";

export const IndividualCinemaPage: React.FC = () => {
  const { venueSlug } = useParams();

  const [venue, setVenue] = React.useState<ParentVenue | null>(null);
  const [venueNotFound, setVenueNotFound] = React.useState(false);

  const venuesNearLocation: ChildVenue[] = React.useMemo(() => {
    if (!venue) return [];
    return venue.nearby_venues;
  }, [venue]);

  const validVenueSlugs = React.useMemo(
    () => venuesNearLocation.map((v) => v.venue_slug),
    [venuesNearLocation],
  );

  const initialSelectedVenues = React.useMemo(
    () => (venueSlug ? [venueSlug] : []),
    [venueSlug],
  );

  const controller = useFilmListingsController({
    contextSlug: venueSlug ?? "",
    initialSelectedVenues,
    validVenueSlugs,
    defaultOptimise: false,
    enabled: Boolean(venueSlug && venue),
  });

  React.useEffect(() => {
    async function loadVenue() {
      try {
        if (!venueSlug) {
          setVenueNotFound(true);
          return;
        }

        const venue = await getIndividualVenue(venueSlug);
        if (!venue) {
          setVenueNotFound(true);
          return;
        }

        setVenue(venue);
      } catch (e) {
        setVenueNotFound(true);
      }
    }

    loadVenue();
  }, [venueSlug]);

  if (venueNotFound) {
    return (
      <NotFoundPage
        title="Cinema not found | CinemaGuide"
        description="This cinema page doesn’t exist (or may have been removed)."
      />
    );
  }

  if (controller.isInitialLoading || !venue) {
    return <LoadingElementInBox />;
  }

  return (
    <>
      <CssBaseline />

      <StandardPageHead
        title={`${venue.venue_name} | CinemaGuide`}
        description={`Browse cinema showtimes for ${venue.venue_name} and all nearby cinemas.`}
        pageUrl={`https://cinemaguideireland.com/cinemas/${venueSlug}`}
      />

      <Box
        component="main"
        sx={{
          bgcolor: "background.default",
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TitleElement
          headerOneText={venue.venue_name}
          headerTwoText={`Browse cinema showtimes for ${venue.venue_name} and all nearby cinemas.`}
        />

        <LandingPageDescriptionBoxes
          locationText={`${venue.venue_name}`}
          boxOneText={`Every movie showtime for all cinemas near ${venue.venue_name} on one page. No more trawling between different websites.`}
          boxTwoText={`Easily buy tickets for every film showing near ${venue.venue_name}. Compare different showtimes to get the cheapest cinema tickets.`}
          boxThreeText={`Browse and search for every film that is scheduled to be shown near ${venue.venue_name}, from blockbusters to independent cinema.`}
        />

        <Box
          sx={{
            pt: { xs: 5, md: 3 },
            pb: { xs: 2, md: 2 },
            textAlign: "center",
          }}
        >
          <LinkToCinemaSelectPage
            leadText={"Want to view showtimes elsewhere? "}
            appendHref={false}
          />
        </Box>
      </Box>

      <FilmListingsSection
        controller={controller}
        venuesNearLocation={venuesNearLocation}
        selectAllVenuesLabel={`SELECT ALL VENUES NEAR ${venue.venue_name.toUpperCase()}`}
      />

      <noscript>
        <div>
          CinemaGuide requires JavaScript to display the latest showtimes.
          Please enable JavaScript in your browser.
        </div>
      </noscript>
    </>
  );
};
